import { ChangeDetectorRef, Component, DestroyRef, EventEmitter, inject, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild, } from '@angular/core';

import { ColourHelper, Constants, documentTypes, PROJECT_IMAGE_SIZES, SnackBarHelperComponent, TEXT, ValidatorHelper } from '../../../../../../../helpers';
import { GetColourValidation } from '../../../../../../../interfaces';
import { BrandModel, ColourModel } from '../../../../../../../models';
import { ColourService } from '../../../../../../../services/colour.service';
import { DialogButtonConfig, DialogComponent } from '@mos-core/components/dialog/dialog.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, switchMap, tap } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { LogoUploaderComponent } from '../../../../../../../dialog/components';
import { ImageService } from '../../../../../../../services/image.service';

@Component({
	selector: 'add-edit-colour-dialog',
	templateUrl: './add-edit-colour-dialog.component.html',
	styleUrls: ['./add-edit-colour-dialog.component.scss'],
})
export class AddEditColourDialogComponent implements OnInit, OnChanges {
	@ViewChild('colourDialog') public colourDialog!: DialogComponent;
	@ViewChild('logoUploaderComponent', { static: true }) public logoUploaderComponent: LogoUploaderComponent;

	@Input() public data: {
		brands: BrandModel[],
		colour?: ColourModel,
	};
	@Input() public isContractsAndAbove: boolean = true;

	@Output() public colourUpdated: EventEmitter<ColourModel> = new EventEmitter<ColourModel>();

	public brand: BrandModel = undefined;
	public brands: BrandModel[];
	public colour: ColourModel = new ColourModel({
		name: undefined,
		brand: undefined,
		code: undefined,
		base: undefined,
		fileKey: undefined,
		fileUrl: undefined,
		hexColour: '#000000',
		red: 0,
		green: 0,
		blue: 0,
		isActive: true,
	});
	public dialogButtons: DialogButtonConfig[] = [
		{ label: 'Save', action: this.saveColour.bind(this), type: 'primary' },
		{ label: 'Close', action: this.closeColourDialog.bind(this), type: 'normal' },
	];
	public file: File | Blob;
	public formState = Constants.FORM_STATES.ADD;
	public FORM_STATES = Constants.FORM_STATES;
	public loading: boolean = false;
	public textConstants: typeof TEXT = TEXT;
	public validators;

	private destroyRef = inject(DestroyRef);

	constructor(
		private colourService: ColourService,
		private changeDetectorRef: ChangeDetectorRef,
		public snack: SnackBarHelperComponent,
		private imageService: ImageService,
	) {}

	public ngOnInit(): void {
		this.validators = GetColourValidation();
	}

	public ngOnChanges(changes: SimpleChanges): void {
		if (changes.data && this.data?.colour) {
			this.colour = this.data.colour;
			this.formState = this.colour.documentType === documentTypes.master ? this.FORM_STATES.VIEW : this.colour.id ? this.FORM_STATES.EDIT : this.FORM_STATES.ADD;
			this.dialogButtons = [
				{ label: this.colour.documentType === documentTypes.master ? 'Clone' : 'Save', action: this.saveColour.bind(this), type: 'primary' },
				{ label: 'Close', action: this.closeColourDialog.bind(this), type: 'normal' },
			];
		}
	}

	/**
	 * For master items - Updates the dialog and colour state to prepare cloning of the master colour
	 * For entity items - saves the current colour which is being either edited, added or cloned.
	 */
	public saveColour(): void {
		if (this.colour.documentType === this.documentTypes.master) {
			// Set the colours parent to this colour
			this.colour.parent = this.colour.id;
			// Unset id and change document type to entity
			this.colour.id = undefined;
			this.colour.documentType = this.documentTypes.entity;
			// Update form state to create colour with prefilled master data
			this.formState = this.FORM_STATES.ADD;
			this.dialogButtons = [
				{ label: 'Save', action: this.saveColour.bind(this), type: 'primary' },
				{ label: 'Close', action: this.closeColourDialog.bind(this), type: 'normal' },
			];
		} else {
			const errors = ValidatorHelper.checkErrors(this.validators);
			if (errors) {
				this.snack.snackError(errors);
				return;
			}

			this.loading = true;
			const savingColourObservable: Observable<ColourModel> = this.colour && !this.colour.id
				? this.colourService.postCreate(this.colour)
				: this.colourService.postEdit(this.colour);

			savingColourObservable.pipe(takeUntilDestroyed(this.destroyRef),
				switchMap((colour) => {
					if (this.file instanceof File) {
						const fd = new FormData();
						fd.append('id', colour.id);
						fd.append('file', this.file, this.file.name);
						return this.colourService.postUploadFile(fd);
					} else {
						return of(colour);
					}
				}),
				tap((colour: ColourModel) => {
					this.colour = colour;
					this.rgbValidCheck();
					this.updateColourPanel(false);
					this.colourUpdated.emit(colour);
				}), finalize(() => {
					this.loading = false;
					this.closeColourDialog();
				})
			).subscribe();
		}
	}

	/**
	 * Toggles the isActive status of the colour.
	 */
	public toggleIsActiveStatus(): void {
		this.loading = true;
		this.colourService.postEditActive(this.colour.id, !this.colour.isActive).pipe(takeUntilDestroyed(this.destroyRef),
			tap((colour: ColourModel) => {
				this.colour = colour;
				this.rgbValidCheck();
				this.updateColourPanel(false);
				this.colourUpdated.emit(this.colour);
			}), finalize(() => {
				this.loading = false;
				this.closeColourDialog();
			})
		).subscribe();
	}

	public openColourDialog(): void {
		this.colourDialog.open();
	}

	public closeColourDialog() {
		this.removeUploadedFile();
		this.colourDialog.close();
	}

	/**
	 * Update the colour panel background colour
	 */
	public updateColourPanel(isHex): void {
		//RGB preview for Edit Colour form
		this.changeDetectorRef.detectChanges();
		//This catch is needed to stop console errors from popping
		//up when a user deletes the numbers in the input field
		if (isHex) {
			if (ColourHelper.hexToRgb(this.colour.hexColour)) {
				this.updateColorsForRgb(this.colour.hexColour);
			} else {
				this.colour.hexColour = undefined;
			}
		} else {
			this.colour.hexColour = this.rgbToHex(this.colour.red, this.colour.green, this.colour.blue);
		}
	}

	/**
	 * Converts RGB numbers to Hex string
	 */
	private rgbToHex(r: number, g: number, b: number): string {
		return '#' + ColourHelper.colorNumberToHex(r) + ColourHelper.colorNumberToHex(g) + ColourHelper.colorNumberToHex(b);
	}

	private rgbValidCheck(): void {
		//sets RGB to 0 if both rgb and hex are invalid
		if (!this.colour.red || !this.colour.green || !this.colour.blue) {
			if (ColourHelper.hexToRgb(this.colour.hexColour)) {
				this.updateColorsForRgb(this.colour.hexColour);
			} else {
				this.colour.red = 0;
				this.colour.green = 0;
				this.colour.blue = 0;
			}
		}
		this.updateColourPanel(false);
	}

	private updateColorsForRgb(hexColour): void {
		this.colour.red = ColourHelper.hexToRgb(hexColour).r;
		this.colour.green = ColourHelper.hexToRgb(hexColour).g;
		this.colour.blue = ColourHelper.hexToRgb(hexColour).b;
	}

	public setFile(file: File): void {
		if (!file) {
			this.file = null;
			return;
		}

		const blobObservable = this.imageService.downscaleImage(
			file,
			PROJECT_IMAGE_SIZES.thumbnail.width,
			PROJECT_IMAGE_SIZES.thumbnail.height,
			PROJECT_IMAGE_SIZES.thumbnail.quality,
		);

		blobObservable.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(blob => {
			this.file = new File([blob], file.name, { type: blob.type });
		});
	}

	public removeUploadedFile() {
		this.file = undefined;
		this.colour.fileKey = null;
		this.colour.fileUrl = null;
		if (this.logoUploaderComponent) {
			this.logoUploaderComponent.removeImageUrl();
		}
	}

	protected readonly documentTypes = documentTypes;
}
